import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Check, X, Loader } from "lucide-react";
import { useInit } from "@follosoft/react-components";
import { Button } from "@/components/ui/button";
import { LanguageSelector } from "@/components/LanguageSelector";
import { formatCurrency } from "@/utils/currency";
import { ContractUpdate } from "../types";
import SummaryDiff from "./summary-diff";

interface Props {
  contractUpdate: ContractUpdate;
  accepting: boolean;
  refusing: boolean;
  onAccept: () => void;
  onRefuse: () => void;
  actionError: string | null;
}

const ContractUpdateCard: FC<Props> = ({
  contractUpdate,
  accepting,
  refusing,
  onAccept,
  onRefuse,
  actionError,
}) => {
  const { t, i18n } = useTranslation(["contract-updates"]);
  const { account } = useInit();

  const logo = contractUpdate.company_logo ?? account?.attributes?.logo;
  const busy = accepting || refusing;

  return (
    <div className="flex flex-col flex-1 justify-between gap-6">
      <div className="flex items-center justify-between">
        {logo ? (
          <img
            alt={contractUpdate.company_name}
            src={logo}
            className="h-8 max-w-[200px] object-contain"
          />
        ) : (
          <p className="text-lg font-bold text-primary">
            {contractUpdate.company_name}
          </p>
        )}
        <LanguageSelector />
      </div>

      <h1 className="text-2xl font-bold text-center">
        {t(
          "contract-updates:page-title",
          "Update to your contract #{{contractId}} pricing!",
          { contractId: contractUpdate.contract_id },
        )}
      </h1>

      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3 text-base">
          <p>
            {t("contract-updates:greeting", "Hello {{name}}", {
              name: contractUpdate.client_name,
            })}
          </p>
          <p>
            {t(
              "contract-updates:intro",
              "We've updated the pricing on your contract #{{contractId}} and would like your approval before applying the changes.",
              { contractId: contractUpdate.contract_id },
            )}
          </p>
        </div>

        <SummaryDiff
          previousTotal={contractUpdate.previous_total}
          newTotal={contractUpdate.new_total}
          difference={contractUpdate.difference}
        />

        {contractUpdate.remaining_invoices_count > 0 && (
          <div className="flex flex-col gap-3">
            <h3 className="text-lg font-semibold">
              {t(
                "contract-updates:how-applied-title",
                "How the change will be applied",
              )}
            </h3>
            <p className="text-base">
              {t(
                "contract-updates:how-applied-body",
                "The difference will be spread evenly across your {{count}} remaining unpaid invoices (≈ {{amount}} per invoice).",
                {
                  count: contractUpdate.remaining_invoices_count,
                  amount: formatCurrency(contractUpdate.redistribution_per_invoice, i18n.language),
                },
              )}
            </p>
          </div>
        )}

        <p className="text-base">
          {t(
            "contract-updates:cta-instruction",
            'Please review and let us know if you accept these changes by clicking the "Review & Accept" button below or by replying to the email you received.',
          )}
        </p>

        {actionError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {actionError}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <Button
            type="button"
            size="lg"
            className="flex-1 h-12"
            disabled={busy}
            onClick={onAccept}
          >
            {accepting ? (
              <Loader className="size-4 animate-spin" />
            ) : (
              <Check className="size-4" />
            )}
            {t("contract-updates:accept-button", "Review & Accept")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="flex-1 h-12 text-rose-600 hover:text-rose-700"
            disabled={busy}
            onClick={onRefuse}
          >
            {refusing ? (
              <Loader className="size-4 animate-spin" />
            ) : (
              <X className="size-4 text-foreground" />
            )}
            {t("contract-updates:refuse-button", "Refuse")}
          </Button>
        </div>

        <p className="text-base whitespace-pre-line">
          {t("contract-updates:signature", "Thank you,\n{{name}}", {
            name: contractUpdate.sender_name,
          })}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
        <p className="text-base">
          {t("contract-updates:questions", "Got questions?")}
        </p>
        {contractUpdate.company_email && (
          <Button asChild variant="link" className="text-primary px-1">
            <a href={`mailto:${contractUpdate.company_email}`}>
              {t("contract-updates:contact-us", "Contact us")}
            </a>
          </Button>
        )}
      </div>
    </div>
  );
};

export default ContractUpdateCard;
